import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';
import {  BookOpen, Target, Save } from 'lucide-react'; 
import COEHeader from './COEHeader';

const ProgramCourseOutcomes = () => {
  const location = useLocation();
  const { orgInfo } = location.state || {};

  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedProgramType, setSelectedProgramType] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [totalPOs, setTotalPOs] = useState('');
  const [totalCOs, setTotalCOs] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Data Fetching Logic (Kept your existing logic)
  useEffect(() => {
    const tenantId = localStorage.getItem('tenantId') || orgInfo?.tenantId;
    if (!tenantId) return;
    api.get('/api/program-types', { params: { tenantId, status: 'active' } })
      .then(res => setProgramTypes(res.data.programTypes || []))
      .catch(() => setProgramTypes([]));
  }, [orgInfo]);

  useEffect(() => {
    const tenantId = localStorage.getItem('tenantId') || orgInfo?.tenantId;
    if (!tenantId || !selectedProgramType) {
      setPrograms([]);
      return;
    }
    api.get('/api/programs', { params: { tenantId, programTypeId: selectedProgramType, status: 'active' } })
      .then(res => setPrograms(res.data.programs || []))
      .catch(() => setPrograms([]));
  }, [orgInfo, selectedProgramType]);

  useEffect(() => {
    if (selectedProgramType && selectedProgram) {
      api.get(`/api/coe/outcomes/program-outcome`, {
        params: { programTypeId: selectedProgramType, programId: selectedProgram }
      }).then(res => setTotalPOs(res.data.data?.totalPOs || ''));
      api.get(`/api/coe/outcomes/course-outcome`, {
        params: { programId: selectedProgram }
      }).then(res => setTotalCOs(res.data.data?.totalCOs || ''));
    } else {
      setTotalPOs('');
      setTotalCOs('');
    }
  }, [selectedProgramType, selectedProgram]);

  const handleTotalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProgramType || !selectedProgram) return;
    setIsSaving(true);
    try {
      await api.post(`/api/coe/outcomes/program-outcome`, {
        programTypeId: selectedProgramType,
        programId: selectedProgram,
        totalPOs: Number(totalPOs)
      });
      await api.post(`/api/coe/outcomes/course-outcome`, {
        programId: selectedProgram,
        totalCOs: Number(totalCOs)
      });
      // Add success toast here if available
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-12">
      <COEHeader orgInfo={orgInfo || {}} />

      <main className="max-w-5xl mx-auto px-4 pt-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <form onSubmit={handleTotalSubmit} className="space-y-6">
          
          {/* Step 1: Context Selection */}
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-slate-100 bg-slate-50/50">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[10px]">01</span>
                Program Selection
              </h2>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 ml-1">Program Type</label>
                <select 
                  className="w-full h-12 bg-white border border-slate-300 rounded-xl px-4 text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer"
                  value={selectedProgramType} 
                  onChange={e => setSelectedProgramType(e.target.value)}
                >
                  <option value="">Choose a type...</option>
                  {programTypes.map(pt => <option key={pt._id} value={pt._id}>{pt.name}</option>)}
                </select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 ml-1">Program Name</label>
                <select 
                  className="w-full h-12 bg-white border border-slate-300 rounded-xl px-4 text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all appearance-none cursor-pointer disabled:bg-slate-50 disabled:text-slate-400"
                  value={selectedProgram} 
                  onChange={e => setSelectedProgram(e.target.value)} 
                  disabled={!selectedProgramType}
                >
                  <option value="">Select a program...</option>
                  {programs.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Step 2: Outcome Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Input Card */}
            <section className="md:col-span-1 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between">
              <div className="space-y-6">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[10px]">02</span>
                  Define Totals
                </h2>
                
                <div className="space-y-4">
                  <div className="relative group">
                    <label className="text-[11px] font-bold text-indigo-600 uppercase absolute -top-2 left-3 bg-white px-1 z-10">Total POs</label>
                    <input
                      type="number"
                      className="w-full h-12 border border-slate-300 rounded-xl px-4 pt-1 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                      placeholder="0"
                      value={totalPOs}
                      onChange={e => setTotalPOs(e.target.value)}
                      required
                    />
                  </div>

                  <div className="relative group">
                    <label className="text-[11px] font-bold text-emerald-600 uppercase absolute -top-2 left-3 bg-white px-1 z-10">Total COs</label>
                    <input
                      type="number"
                      className="w-full h-12 border border-slate-300 rounded-xl px-4 pt-1 focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                      placeholder="0"
                      value={totalCOs}
                      onChange={e => setTotalCOs(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSaving || !selectedProgram}
                className="mt-8 w-full bg-slate-900 text-white h-12 rounded-xl font-bold hover:bg-indigo-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none shadow-lg shadow-indigo-200"
              >
                {isSaving ? (
                   <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <><Save size={18} /> Save Config</>
                )}
              </button>
            </section>

            {/* Visualizer Card */}
            <section className="md:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-6">
                   <h2 className="text-slate-800 font-bold flex items-center gap-2">
                     Preview Mapping
                   </h2>
                   <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-1 rounded-full font-bold uppercase">Live Preview</span>
                </div>

                <div className="space-y-8 flex-1">
                  {/* PO List */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Target size={16} className="text-indigo-500" />
                      <span className="text-sm font-bold text-slate-700">Program Outcomes</span>
                    </div>
                    {totalPOs > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {Array.from({ length: Number(totalPOs) }, (_, i) => (
                          <div key={i} className="px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-bold border border-indigo-100 animate-in zoom-in-95 duration-300">
                            PO-{String(i + 1).padStart(2, '0')}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-12 border-2 border-dashed border-slate-100 rounded-xl flex items-center justify-center text-slate-400 text-xs italic">No POs defined</div>
                    )}
                  </div>

                  {/* CO List */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen size={16} className="text-emerald-500" />
                      <span className="text-sm font-bold text-slate-700">Course Outcomes</span>
                    </div>
                    {totalCOs > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {Array.from({ length: Number(totalCOs) }, (_, i) => (
                          <div key={i} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold border border-emerald-100 animate-in zoom-in-95 duration-300">
                            CO-{String(i + 1).padStart(2, '0')}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="h-12 border-2 border-dashed border-slate-100 rounded-xl flex items-center justify-center text-slate-400 text-xs italic">No COs defined</div>
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ProgramCourseOutcomes;